// src/components/wholesaler/OrderHistory.js
import { useState } from 'react';
import { Package, ChevronDown, ChevronUp, Truck, Clock, CheckCircle, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
  'New': { color: 'bg-blue-100 text-blue-800', icon: Clock },
  'Processing': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'Filled': { color: 'bg-purple-100 text-purple-800', icon: Package },
  'Shipped': { color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  'Delivered': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'Cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle }
};

export function OrderHistory({ orders, loading }) {
  const [expandedOrder, setExpandedOrder] = useState(null);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-gray-300 border-t-black rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading orders...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6">Order History</h2>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Package size={48} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No orders yet</h3>
          <p className="text-gray-500">Your orders will appear here once you place them.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Order History</h2>
      
      <div className="space-y-4">
        {orders.map(order => {
          const isExpanded = expandedOrder === order.id;
          const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG['New'];
          const StatusIcon = statusConfig.icon;
          const orderDate = order.createdAt?.toDate?.() || new Date(order.createdAt);

          return (
            <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Order Header - Always visible */}
              <button
                onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="text-left">
                    <p className="font-semibold">Order #{order.id.slice(-8).toUpperCase()}</p>
                    <p className="text-sm text-gray-500">
                      {orderDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                    <StatusIcon size={14} />
                    {order.status}
                  </span>
                  <span className="font-bold">${order.totalWithFee?.toFixed(2) || order.total?.toFixed(2)}</span>
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>

              {/* Order Details - Expanded */}
              {isExpanded && (
                <div className="border-t p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Items */}
                    <div>
                      <h4 className="font-semibold mb-3">Items</h4>
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.name} × {item.quantity}</span>
                            <span>${(item.wholesalePrice * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="border-t mt-3 pt-3 space-y-1">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal</span>
                          <span>${order.subtotal?.toFixed(2) || order.total?.toFixed(2)}</span>
                        </div>
                        {order.ccFee && (
                          <div className="flex justify-between text-sm text-gray-500">
                            <span>CC Fee</span>
                            <span>${order.ccFee.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold">
                          <span>Total Paid</span>
                          <span>${order.totalWithFee?.toFixed(2) || order.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Shipping Info */}
                    <div>
                      <h4 className="font-semibold mb-3">
                        {order.isDropship ? 'Drop Ship Address' : 'Shipping Address'}
                      </h4>
                      {order.shippingAddress && (
                        <div className="text-sm text-gray-600">
                          {order.shippingAddress.name && (
                            <p className="font-medium">{order.shippingAddress.name}</p>
                          )}
                          {order.shippingAddress.company && (
                            <p>{order.shippingAddress.company}</p>
                          )}
                          <p>{order.shippingAddress.street}</p>
                          <p>
                            {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                          </p>
                        </div>
                      )}

                      {order.trackingNumber && (
                        <div className="mt-4">
                          <h4 className="font-semibold mb-2">Tracking</h4>
                          <p className="text-sm text-blue-600">{order.trackingNumber}</p>
                        </div>
                      )}

                      {order.notes && (
                        <div className="mt-4">
                          <h4 className="font-semibold mb-2">Order Notes</h4>
                          <p className="text-sm text-gray-600">{order.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Info */}
                  {order.paymentStatus && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Payment:</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          order.paymentStatus === 'succeeded' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.paymentStatus === 'succeeded' ? 'Paid' : order.paymentStatus}
                        </span>
                        {order.stripePaymentId && (
                          <span className="text-gray-400">({order.stripePaymentId.slice(-8)})</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
